import { useMemo, useState, type FormEvent } from 'react';
import { Surface, ghostLinkStyle, inputStyle, primaryButtonStyle } from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { customerTagsRepoFor } from '../lib/repositories/customer-tags-repo.js';
import { logger } from '../lib/logger.js';
import type { CustomerTag } from '@kilat/shared';

/**
 * /loyalty/tags — operator-defined customer tag templates.
 *
 * Tags compose with two other surfaces:
 *   - /customers detail panel applies/removes them on a per-customer basis.
 *   - /loyalty/vouchers `customerTagIn` targeting field references their ids.
 *
 * Delete is soft (archive flag) so existing voucher targeting + customer
 * applications keep referencing the same id without dangling.
 *
 * Premium-logical: explicit loading / empty / error / saving / archived states
 * — every chip + action label tells the operator what's about to happen.
 */
export function TagsPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const subscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = customerTagsRepoFor(tenantId);
    return (cb: (vs: CustomerTag[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);

  const {
    data: tags,
    loading,
    error,
  } = useTrackedSnapshot<CustomerTag[]>(['customerTags', tenantId], subscribe);

  if (!tenantId) {
    return (
      <Screen
        title={t.t('dashboard.tags.signInTitle')}
        body={t.t('dashboard.tags.signInBody')}
        ctaLabel={t.t('dashboard.states.backToQueue')}
        to="/queue"
      />
    );
  }

  const list = (tags ?? []).slice().sort((a, b) => {
    if (a.archived !== b.archived) return a.archived ? 1 : -1;
    const am = a.label['en'] ?? a.id;
    const bm = b.label['en'] ?? b.id;
    return am.localeCompare(bm);
  });

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.tags.title')}
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {loading
              ? t.t('common.loading')
              : t.t('dashboard.tags.countSummary', {
                  active: list.filter((tg) => !tg.archived).length,
                  archived: list.filter((tg) => tg.archived).length,
                })}
          </p>
        </div>
        <Link to="/customers" style={ghostLinkStyle}>
          {t.t('dashboard.tags.backToCustomers')}
        </Link>
      </header>

      <div className="layout-split-aside">
        <Surface as="section">
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t.t('dashboard.tags.yourTags')}
          </h2>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {loading ? (
              <InlineNote>{t.t('dashboard.tags.loadingLive')}</InlineNote>
            ) : error ? (
              <InlineNote tone="error">
                {t.t('dashboard.tags.loadError', { msg: String(error.message ?? error) })}
              </InlineNote>
            ) : list.length === 0 ? (
              <EmptyState
                title={t.t('dashboard.tags.emptyTitle')}
                body={t.t('dashboard.tags.emptyBody')}
              />
            ) : (
              list.map((tg) => <TagRow key={tg.id} tenantId={tenantId} tag={tg} />)
            )}
          </div>
        </Surface>

        <Surface as="section">
          <NewTagForm tenantId={tenantId} />
        </Surface>
      </div>
    </main>
  );
}

function TagRow({ tenantId, tag }: { readonly tenantId: string; readonly tag: CustomerTag }) {
  const t = useI18n();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function toggleArchive() {
    setBusy(true);
    setErr(null);
    try {
      await customerTagsRepoFor(tenantId).update(tag.id, {
        archived: !tag.archived,
        updatedAt: Timestamp.now(),
      });
    } catch (e) {
      logger.error('tag archive toggle failed', { id: tag.id, error: String(e) });
      setErr(String((e as Error).message ?? t.t('dashboard.tags.errCouldNotSave')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: tag.archived ? 'var(--tint-subtle)' : 'var(--color-surface)',
        opacity: tag.archived ? 0.7 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', minWidth: 0 }}>
        <TagChip label={tag.label['en'] ?? tag.id} colour={tag.colour ?? undefined} />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 500 }}>{tag.label['en'] ?? tag.id}</p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.tags.rowIdLabel', { id: tag.id })}{' '}
            {tag.archived ? t.t('dashboard.tags.archivedSuffix') : ''}
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        {err ? (
          <span role="alert" style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}>
            {err}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => void toggleArchive()}
          disabled={busy}
          style={miniGhostButtonStyle}
        >
          {busy
            ? '…'
            : tag.archived
              ? t.t('dashboard.tags.restore')
              : t.t('dashboard.tags.archive')}
        </button>
      </div>
    </div>
  );
}

function NewTagForm({ tenantId }: { readonly tenantId: string }) {
  const t = useI18n();
  const [label, setLabel] = useState('');
  const [colour, setColour] = useState('#0d7a4f');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdLabel, setCreatedLabel] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmed = label.trim();
    if (!trimmed) {
      setError(t.t('dashboard.tags.errNoLabel'));
      return;
    }
    setSubmitting(true);
    try {
      const now = Timestamp.now();
      const id = slugifyTagLabel(trimmed) || `tag-${Math.random().toString(36).slice(2, 8)}`;
      await customerTagsRepoFor(tenantId).set(id, {
        id,
        label: { en: trimmed },
        ...(colour ? { colour } : {}),
        archived: false,
        createdAt: now,
        updatedAt: now,
      });
      setCreatedLabel(trimmed);
      setLabel('');
    } catch (e2) {
      logger.error('tag create failed', { error: String(e2) });
      setError((e2 as Error).message ?? t.t('dashboard.tags.errCreateFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <header>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.tags.newTagHeading')}
        </h2>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.tags.newTagSubtitle')}
        </p>
      </header>

      {createdLabel ? (
        <p
          role="status"
          style={{
            margin: 0,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {t.t('dashboard.tags.createdNote', { label: createdLabel })}
        </p>
      ) : null}

      <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <span style={fieldLabel}>{t.t('dashboard.tags.labelFieldLabel')}</span>
        <input
          required
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t.t('dashboard.tags.labelPlaceholder')}
          style={inputStyle}
        />
      </label>

      <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <span style={fieldLabel}>{t.t('dashboard.tags.colourFieldLabel')}</span>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input
            type="color"
            value={colour}
            onChange={(e) => setColour(e.target.value)}
            style={{
              width: 48,
              height: 36,
              padding: 0,
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-sm)',
            }}
          />
          <TagChip label={label.trim() || t.t('dashboard.tags.previewFallback')} colour={colour} />
        </div>
      </label>

      {error ? (
        <p
          role="alert"
          style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
        >
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        style={{
          ...primaryButtonStyle,
          opacity: submitting ? 0.6 : 1,
          cursor: submitting ? 'wait' : 'pointer',
        }}
      >
        {submitting ? t.t('dashboard.tags.creating') : t.t('dashboard.tags.createTag')}
      </button>
    </form>
  );
}

function TagChip({ label, colour }: { readonly label: string; readonly colour?: string }) {
  const bg = colour ? `${colour}1f` : 'var(--color-border)';
  const fg = colour ?? 'var(--color-on-surface-muted)';
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: bg,
        color: fg,
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function slugifyTagLabel(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
}

function EmptyState({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <div style={{ textAlign: 'center', padding: 'var(--space-6) var(--space-4)' }}>
      <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {body}
      </p>
    </div>
  );
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: 'var(--space-3) var(--space-4)',
        fontSize: 'var(--text-meta)',
        color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </p>
  );
}

function Screen({
  title,
  body,
  ctaLabel,
  to,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly to: string;
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center' }}>
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        <Link
          to={to}
          style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
        >
          {ctaLabel}
        </Link>
      </div>
    </main>
  );
}

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};
